/* flp-shared.js — custom elements usados por mais de uma seção.
   Shopify só carrega o {% javascript %} de uma seção nas páginas em que ela existe,
   então tudo que é compartilhado (busca + coleção, carrosséis da PDP) vive aqui. */
(function () {

  // Carrossel genérico: setas que andam um card e se desligam nas pontas.
  // Serve qualquer seção que tenha [data-track] e [data-scroll].
  class FlpCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.arrows = Array.from(this.querySelectorAll('[data-scroll]'));
      if (!this.track) return;

      this.arrows.forEach((arrow) => {
        arrow.addEventListener('click', () => this.step(arrow.dataset.scroll));
      });

      this.track.addEventListener('scroll', () => this.sync(), { passive: true });
      new ResizeObserver(() => this.sync()).observe(this.track);
      this.sync();
    }

    step(direction) {
      const card = this.track.firstElementChild;
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = card.getBoundingClientRect().width + gap;
      this.track.scrollBy({ left: direction === 'next' ? distance : -distance, behavior: 'smooth' });
    }

    sync() {
      const max = this.track.scrollWidth - this.track.clientWidth - 1;
      const atStart = this.track.scrollLeft <= 0;
      const atEnd = this.track.scrollLeft >= max;

      // Qualquer seção pode se pendurar nisso pra mostrar ou esconder a dica
      // de arraste, sem escrever JS próprio.
      this.dataset.scrolled = max <= 1 ? 'none' : atEnd ? 'end' : atStart ? 'start' : 'middle';

      this.arrows.forEach((arrow) => {
        arrow.disabled = arrow.dataset.scroll === 'next' ? atEnd : atStart;
      });
    }
  }

  if (!customElements.get('flp-carousel')) {
    customElements.define('flp-carousel', FlpCarousel);
  }


  class CollectionPage extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.dialog = this.querySelector('[data-facets-panel]');
      this.sheet = this.querySelector('[data-sheet]');
      this.grid = this.querySelector('[data-grid]');
      this.tab = 'filters';

      this.applyDensity(this.storedDensity());

      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-open="facets"]');
        if (opener) return this.open(opener);
        if (event.target.closest('[data-close]')) return this.close();

        const tab = event.target.closest('[data-tab]');
        if (tab) return this.showTab(tab.dataset.tab);

        const dense = event.target.closest('[data-density]');
        if (dense) return this.applyDensity(Number(dense.dataset.density), true);

        const link = event.target.closest('[data-facet-link]');
        if (link) {
          event.preventDefault();
          this.load(link.href);
        }
      });

      this.dialog?.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });

      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });

      // Marcar um filtro ja busca; digitar preco espera a pessoa parar.
      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-facet-form]')) this.submit();
      });

      this.addEventListener('input', (event) => {
        const field = event.target.closest('[data-facet-form] input[type="number"]');
        if (!field) return;
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => this.submit(), 500);
      });

      window.addEventListener('popstate', () => this.load(location.href, false));
    }

    /* ---------- densidade ---------- */

    storedDensity() {
      const wide = window.matchMedia('(min-width: 1024px)').matches;
      try {
        const saved = Number(localStorage.getItem(wide ? 'flp:cols-lg' : 'flp:cols-sm'));
        if (saved) return saved;
      } catch {
        /* sem storage: cai no padrao da secao */
      }
      return wide
        ? Number(getComputedStyle(this).getPropertyValue('--col-cards-desktop')) || 4
        : Number(getComputedStyle(this).getPropertyValue('--col-cards-mobile')) || 2;
    }

    applyDensity(value, persist) {
      this.style.setProperty('--col-cards', value);
      this.querySelectorAll('[data-density]').forEach((button) => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.density) === value));
      });
      if (!persist) return;
      try {
        const wide = window.matchMedia('(min-width: 1024px)').matches;
        localStorage.setItem(wide ? 'flp:cols-lg' : 'flp:cols-sm', String(value));
      } catch {
        /* nada a fazer */
      }
    }

    /* ---------- gaveta ---------- */

    showTab(name) {
      this.tab = name;
      this.querySelectorAll('[data-tab]').forEach((button) => {
        button.setAttribute('aria-selected', String(button.dataset.tab === name));
      });
      this.querySelectorAll('[data-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.panel !== name;
      });
      this.querySelector('[data-facets]')?.scrollTo({ top: 0 });
    }

    open(trigger) {
      if (!this.dialog || this.dialog.open) return;
      // O botão do trilho decide qual aba abre.
      this.showTab(trigger?.dataset.focus === 'sort' ? 'sort' : 'filters');
      this.dialog.showModal();
      requestAnimationFrame(() => {
        this.dialog.dataset.state = 'open';
      });
    }

    close() {
      if (!this.dialog?.open) return;
      delete this.dialog.dataset.state;

      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        this.dialog.close();
      };
      this.sheet.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 700);
    }

    /* ---------- busca ---------- */

    submit() {
      const form = this.querySelector('[data-facet-form]');
      if (!form) return;

      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });

      this.load(location.pathname + '?' + params.toString());
    }

    async load(url, push = true) {
      this.dataset.loading = 'true';
      this.controller?.abort();
      this.controller = new AbortController();

      const target = new URL(url, location.origin);
      const fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(fetchUrl, { signal: this.controller.signal });
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        const grid = doc.querySelector('[data-grid]');
        const facets = doc.querySelector('[data-facets]');
        if (grid) this.grid.innerHTML = grid.innerHTML;
        if (facets) {
          this.querySelector('[data-facets]').innerHTML = facets.innerHTML;
          this.showTab(this.tab);
        }

        const dot = doc.querySelector('[data-facet-dot]');
        const myDot = this.querySelector('[data-facet-dot]');
        if (dot && myDot) {
          myDot.hidden = dot.hidden;
          myDot.textContent = dot.textContent;
        }

        if (push) history.pushState({}, '', target);
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch {
        /* pedido trocado por um mais novo */
      } finally {
        delete this.dataset.loading;
      }
    }
  }

  if (!customElements.get('collection-page')) {
    customElements.define('collection-page', CollectionPage);
  }

})();
